"use client";

import { useCallback, useEffect, useState } from "react";
import { useSession } from "next-auth/react";
import { useRouter, useSearchParams } from "next/navigation";
import type { EnquiryAlertPayload } from "@/lib/enquiries/enquiry-alert-payload";
import { isCompanyCrewUser } from "@/lib/crew/workspace";
import { playJobAlertLoop, stopJobAlertLoop } from "./job-alert-sound";

export function EnquiryAlertModal() {
  const { data: session } = useSession();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [payload, setPayload] = useState<EnquiryAlertPayload | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [quoteMode, setQuoteMode] = useState(false);
  const [totalGbp, setTotalGbp] = useState("");
  const [hireWeeks, setHireWeeks] = useState("4");
  const companyId = session?.user?.companyId;
  const isCrew = isCompanyCrewUser(session?.user);
  const enquiryAlertId = searchParams.get("enquiryAlert");

  const clearQuery = useCallback(() => {
    if (typeof window === "undefined") return;
    const u = new URL(window.location.href);
    if (u.searchParams.has("enquiryAlert")) {
      u.searchParams.delete("enquiryAlert");
      const next = u.search ? `${u.pathname}${u.search}` : u.pathname;
      router.replace(next);
    }
  }, [router]);

  const dismiss = useCallback(() => {
    stopJobAlertLoop();
    setPayload(null);
    setQuoteMode(false);
    setError("");
    setTotalGbp("");
    setBusy(false);
    clearQuery();
  }, [clearQuery]);

  const showAlert = useCallback((enquiryAlert: EnquiryAlertPayload) => {
    setPayload(enquiryAlert);
    setQuoteMode(false);
    setError("");
    setTotalGbp("");
    playJobAlertLoop();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;

    const onMessage = (event: MessageEvent) => {
      const data = event.data;
      if (!data || typeof data !== "object") return;
      if (data.type === "ENQUIRY_ALERT_SHOW" && data.enquiryAlert) {
        showAlert(data.enquiryAlert as EnquiryAlertPayload);
        return;
      }
      if (data.type === "ENQUIRY_ALERT_PLAY") {
        playJobAlertLoop();
        return;
      }
      if (data.type === "ENQUIRY_ALERT_STOP" || data.type === "JOB_ALERT_STOP") {
        dismiss();
      }
    };

    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [showAlert, dismiss]);

  useEffect(() => {
    if (!companyId || isCrew || !enquiryAlertId || payload?.enquiryId === enquiryAlertId) {
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `/api/company/enquiries/${enquiryAlertId}/alert-summary`,
          { credentials: "include" }
        );
        const data = await res.json().catch(() => ({}));
        if (!cancelled && res.ok && data.enquiryAlert) {
          showAlert(data.enquiryAlert as EnquiryAlertPayload);
        }
      } catch {
        /* ignore */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [companyId, isCrew, enquiryAlertId, payload?.enquiryId, showAlert]);

  useEffect(() => {
    return () => stopJobAlertLoop();
  }, []);

  useEffect(() => {
    if (!payload) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [payload]);

  if (!companyId || isCrew || !payload) return null;

  const siteLine = [payload.siteAddress, payload.sitePostcode].filter(Boolean).join(", ");

  async function runAction(action: string, extra?: Record<string, unknown>) {
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`/api/company/enquiries/${payload!.enquiryId}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action, ...extra }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(typeof data.error === "string" ? data.error : "Action failed");
      }
      dismiss();
      if (action === "quick_quote" && data.quoteId) {
        router.push(`/app/quotes/${data.quoteId}`);
        return;
      }
      if (action === "mark_contacted") {
        router.push("/app/quotes");
        router.refresh();
        return;
      }
      router.refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Action failed");
      setBusy(false);
    }
  }

  function submitQuickQuote(e: React.FormEvent) {
    e.preventDefault();
    const gbp = Number(totalGbp);
    const weeks = Number(hireWeeks);
    if (!Number.isFinite(gbp) || gbp <= 0) {
      setError("Enter a valid total price in £.");
      return;
    }
    void runAction("quick_quote", {
      totalGbp: gbp,
      hireWeeks: Number.isFinite(weeks) && weeks > 0 ? weeks : 4,
    });
  }

  return (
    <div
      className="fixed inset-0 z-[220] flex items-end justify-center bg-black/50 p-4 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="enquiry-alert-title"
    >
      <div className="relative flex max-h-[min(90vh,640px)] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-2xl">
        <div className="border-b border-zinc-100 bg-brand-dark px-5 py-4 pr-12 text-white">
          <p className="text-xs font-semibold uppercase tracking-wide text-brand-yellow">
            New enquiry · {payload.sourceLabel}
          </p>
          <h2 id="enquiry-alert-title" className="mt-1 text-lg font-bold leading-snug">
            {payload.name}
          </h2>
          <p className="mt-1 text-sm text-zinc-300">
            {siteLine || "No site address yet"}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <dl className="space-y-2 text-sm">
            <div className="flex gap-3">
              <dt className="w-24 shrink-0 text-zinc-500">Email</dt>
              <dd className="font-medium text-zinc-900 break-all">{payload.email}</dd>
            </div>
            {payload.phone ? (
              <div className="flex gap-3">
                <dt className="w-24 shrink-0 text-zinc-500">Phone</dt>
                <dd className="font-medium text-zinc-900">{payload.phone}</dd>
              </div>
            ) : null}
            {payload.description ? (
              <div className="flex gap-3">
                <dt className="w-24 shrink-0 text-zinc-500">Details</dt>
                <dd className="text-zinc-800 whitespace-pre-wrap">{payload.description}</dd>
              </div>
            ) : null}
          </dl>

          {quoteMode ? (
            <form id="enquiry-quick-quote" onSubmit={submitQuickQuote} className="mt-5 space-y-3">
              <p className="text-sm font-semibold text-zinc-900">Quick quote</p>
              <label className="block text-sm">
                <span className="text-zinc-600">Total price (£)</span>
                <input
                  type="number"
                  inputMode="decimal"
                  min={1}
                  step="0.01"
                  required
                  value={totalGbp}
                  onChange={(e) => setTotalGbp(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-zinc-300 px-3 py-2.5 text-zinc-900"
                  placeholder="e.g. 1850"
                  autoFocus
                />
              </label>
              <label className="block text-sm">
                <span className="text-zinc-600">Hire weeks</span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={520}
                  value={hireWeeks}
                  onChange={(e) => setHireWeeks(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-zinc-300 px-3 py-2.5 text-zinc-900"
                />
              </label>
              <p className="text-xs text-zinc-500">
                Creates a draft quote with your price so you can review and send.
              </p>
            </form>
          ) : (
            <p className="mt-4 text-sm text-zinc-600">
              Mark interested to keep it in Quotes → Awaiting, or quote now with a quick price.
            </p>
          )}

          {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}
        </div>

        <div className="flex flex-col gap-2 border-t border-zinc-100 bg-zinc-50 px-5 py-4">
          {quoteMode ? (
            <>
              <button
                type="submit"
                form="enquiry-quick-quote"
                disabled={busy}
                className="w-full rounded-xl bg-brand-yellow px-4 py-3 text-sm font-bold text-brand-dark hover:bg-brand-yellow/90 disabled:opacity-50"
              >
                {busy ? "Creating…" : "Create quote"}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  setQuoteMode(false);
                  setError("");
                }}
                className="w-full rounded-xl border border-zinc-300 bg-white px-4 py-3 text-sm font-semibold text-zinc-800 hover:bg-zinc-50 disabled:opacity-50"
              >
                Back
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                disabled={busy}
                onClick={() => setQuoteMode(true)}
                className="w-full rounded-xl bg-brand-yellow px-4 py-3 text-sm font-bold text-brand-dark hover:bg-brand-yellow/90 disabled:opacity-50"
              >
                Quote now
              </button>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void runAction("mark_contacted")}
                  className="rounded-xl border border-zinc-300 bg-white px-4 py-3 text-sm font-semibold text-zinc-800 hover:bg-zinc-50 disabled:opacity-50"
                >
                  Interested
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void runAction("not_interested")}
                  className="rounded-xl border border-zinc-300 bg-white px-4 py-3 text-sm font-semibold text-zinc-600 hover:bg-zinc-50 disabled:opacity-50"
                >
                  Not interested
                </button>
              </div>
            </>
          )}
        </div>

        <button
          type="button"
          onClick={dismiss}
          disabled={busy}
          className="absolute right-3 top-3 rounded-lg p-2 text-zinc-400 hover:bg-white/10 hover:text-white disabled:opacity-40"
          aria-label="Dismiss alert"
        >
          <span className="text-xl leading-none" aria-hidden>
            ×
          </span>
        </button>
      </div>
    </div>
  );
}
